import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Briefcase, User, Mail, Phone, MapPin, Upload, ChevronRight, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { companyInfo } from '../mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'employer' || tab === 'candidate') {
      setActiveTab(tab);
    }
    
    // Scroll to top of page when navigating here
    window.scrollTo(0, 0);
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Us"
        description="Contact Omnia Paratus Talent Solutions for your recruiting and staffing needs. Located in Boston, MA, we serve clients nationwide. Call (978) 340-6952 or submit an inquiry online."
        keywords="contact recruiting agency, staffing firm contact, Boston recruiter contact, hire talent, submit resume, recruiting inquiry, Massachusetts staffing agency"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Let's Connect</h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            Whether you're looking to hire or exploring your<br />next career move, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-6 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
          <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
            <Mail className="h-4 w-4 text-[#D4AF37]" /> {companyInfo.email}
          </a>
          <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
            <Phone className="h-4 w-4 text-[#D4AF37]" /> {companyInfo.phone}
          </a>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#D4AF37]" /> {companyInfo.serviceArea}
          </span>
        </div>
      </section>

      {/* Pathway Selection */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          {!activeTab && (
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#0A1F44] mb-3">How can we help?</h2>
              <p className="text-gray-600">Select the option that best describes you.</p>
            </div>
          )}

          {/* Tab Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            <button
              data-testid="tab-employer"
              onClick={() => setActiveTab('employer')}
              className={`group p-8 rounded-2xl border-2 text-left transition-all duration-300 ${
                activeTab === 'employer'
                  ? 'border-[#D4AF37] bg-white shadow-xl'
                  : 'border-gray-200 bg-white hover:border-[#D4AF37]/50 hover:shadow-lg'
              }`}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                  activeTab === 'employer' ? 'bg-[#D4AF37]' : 'bg-[#0A1F44]'
                }`}>
                  <Briefcase className={`h-7 w-7 ${activeTab === 'employer' ? 'text-[#0A1F44]' : 'text-[#D4AF37]'}`} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1F44]">I'm Hiring</h3>
                  <p className="text-sm text-gray-500">Find exceptional talent</p>
                </div>
              </div>
              {activeTab !== 'employer' && (
                <p className="text-gray-600 text-sm flex items-center gap-1">
                  Start your search <ChevronRight className="h-4 w-4" />
                </p>
              )}
            </button>

            <button
              data-testid="tab-candidate"
              onClick={() => setActiveTab('candidate')}
              className={`group p-8 rounded-2xl border-2 text-left transition-all duration-300 ${
                activeTab === 'candidate'
                  ? 'border-[#D4AF37] bg-white shadow-xl'
                  : 'border-gray-200 bg-white hover:border-[#D4AF37]/50 hover:shadow-lg'
              }`}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors ${
                  activeTab === 'candidate' ? 'bg-[#D4AF37]' : 'bg-[#0A1F44]'
                }`}>
                  <User className={`h-7 w-7 ${activeTab === 'candidate' ? 'text-[#0A1F44]' : 'text-[#D4AF37]'}`} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1F44]">I'm a Candidate</h3>
                  <p className="text-sm text-gray-500">Explore opportunities</p>
                </div>
              </div>
              {activeTab !== 'candidate' && (
                <p className="text-gray-600 text-sm flex items-center gap-1">
                  Get started <ChevronRight className="h-4 w-4" />
                </p>
              )}
            </button>
          </div>

          {/* Forms */}
          {activeTab === 'employer' && <EmployerForm />}
          {activeTab === 'candidate' && <CandidateForm />}
        </div>
      </section>

      <Footer />
    </div>
  );
};


/* ───────────── EMPLOYER FORM ───────────── */
const EmployerForm = () => {
  const [form, setForm] = useState({
    full_name: '', company_name: '', job_title: '', email: '', phone: '', company_website: '',
    role_title: '', industry: '', employment_type: '', location: '', work_setup: '', timeline: '',
    hiring_reason: '', biggest_challenge: '', worked_with_recruiters: '', salary_range: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.company_name || !form.role_title || !form.industry) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('job_description', file);
      const res = await fetch(`${API_URL}/api/contact/employer`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Your inquiry has been submitted!', { description: "We'll be in touch within 24 hours." });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessMessage type="employer" />;

  return (
    <Card data-testid="employer-form-card" className="border-none shadow-2xl">
      <CardContent className="p-8 md:p-10">
        <h2 className="text-2xl font-bold text-[#0A1F44] mb-8">Client Intake Form</h2>
        <form onSubmit={handleSubmit} className="space-y-10">

          {/* Section 1 */}
          <SectionLabel num="1" title="Contact Information" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Full Name" required><Input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" required /></FormField>
            <FormField label="Company Name" required><Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Acme Corp" required /></FormField>
            <FormField label="Job Title" required><Input value={form.job_title} onChange={e => set('job_title', e.target.value)} placeholder="VP of Operations" required /></FormField>
            <FormField label="Email" required><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@acme.com" required /></FormField>
            <FormField label="Phone Number" required><Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" required /></FormField>
            <FormField label="Company Website" optional><Input value={form.company_website} onChange={e => set('company_website', e.target.value)} placeholder="https://acme.com" /></FormField>
          </div>

          {/* Section 2 */}
          <SectionLabel num="2" title="Hiring Needs" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Role Title / Position Needed" required><Input value={form.role_title} onChange={e => set('role_title', e.target.value)} placeholder="Senior Accountant" required /></FormField>
            <FormField label="Industry" required>
              <Select value={form.industry} onValueChange={v => set('industry', v)}>
                <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accounting">Accounting</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="logistics">Logistics</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Employment Type" required>
              <Select value={form.employment_type} onValueChange={v => set('employment_type', v)}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct_hire">Direct Hire</SelectItem>
                  <SelectItem value="talent_pipeline">Exploring Talent Pipeline</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Location of Role" required><Input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Dallas, TX" required /></FormField>
            <FormField label="Work Setup" required>
              <Select value={form.work_setup} onValueChange={v => set('work_setup', v)}>
                <SelectTrigger><SelectValue placeholder="Select setup" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="onsite">Onsite</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="remote">Remote</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Target Timeline" required>
              <Select value={form.timeline} onValueChange={v => set('timeline', v)}>
                <SelectTrigger><SelectValue placeholder="Select timeline" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent (0–30 days)</SelectItem>
                  <SelectItem value="30-60">30–60 days</SelectItem>
                  <SelectItem value="future">Future planning</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Section 3 */}
          <SectionLabel num="3" title="Strategic Insight" />
          <div className="space-y-5">
            <FormField label="What is prompting this hire?"><Textarea value={form.hiring_reason} onChange={e => set('hiring_reason', e.target.value)} placeholder="Growth, backfill, new role..." rows={3} /></FormField>
            <FormField label="Biggest hiring challenge right now?"><Textarea value={form.biggest_challenge} onChange={e => set('biggest_challenge', e.target.value)} placeholder="Finding qualified candidates, retention..." rows={3} /></FormField>
            <FormField label="Have you worked with recruiters before?">
              <Select value={form.worked_with_recruiters} onValueChange={v => set('worked_with_recruiters', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Section 4 */}
          <SectionLabel num="4" title="Optional Details" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Salary Range" optional><Input value={form.salary_range} onChange={e => set('salary_range', e.target.value)} placeholder="$80,000 - $100,000" /></FormField>
            <FormField label="Upload Job Description" optional>
              <FileInput file={file} setFile={setFile} accept=".pdf,.doc,.docx,.txt" />
            </FormField>
          </div>

          <SubmitButton submitting={submitting} label="Submit Inquiry" />
        </form>
      </CardContent>
    </Card>
  );
};


/* ───────────── CANDIDATE FORM ───────────── */
const CandidateForm = () => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', current_location: '',
    area_of_expertise: '', current_role: '', years_of_experience: '', work_authorization: '',
    role_interests: '', preferred_work_setup: '', salary_expectations: '', open_to_relocation: '',
    linkedin_url: '', next_opportunity: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.area_of_expertise) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('resume', file);
      const res = await fetch(`${API_URL}/api/contact/candidate`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Your application has been submitted!', { description: "We'll review your profile and be in touch soon." });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessMessage type="candidate" />;

  return (
    <Card data-testid="candidate-form-card" className="border-none shadow-2xl">
      <CardContent className="p-8 md:p-10">
        <h2 className="text-2xl font-bold text-[#0A1F44] mb-8">Candidate Intake Form</h2>
        <form onSubmit={handleSubmit} className="space-y-10">

          {/* Section 1 */}
          <SectionLabel num="1" title="Basic Information" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Full Name" required><Input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="John Doe" required /></FormField>
            <FormField label="Email" required><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@email.com" required /></FormField>
            <FormField label="Phone" required><Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" required /></FormField>
            <FormField label="Current Location" required><Input value={form.current_location} onChange={e => set('current_location', e.target.value)} placeholder="Dallas, TX" required /></FormField>
          </div>

          {/* Section 2 */}
          <SectionLabel num="2" title="Professional Background" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Area of Expertise" required>
              <Select value={form.area_of_expertise} onValueChange={v => set('area_of_expertise', v)}>
                <SelectTrigger><SelectValue placeholder="Select area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accounting">Accounting</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="logistics">Logistics</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Current Role / Title"><Input value={form.current_role} onChange={e => set('current_role', e.target.value)} placeholder="Staff Accountant" /></FormField>
            <FormField label="Years of Experience" required>
              <Select value={form.years_of_experience} onValueChange={v => set('years_of_experience', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-2">0–2 years</SelectItem>
                  <SelectItem value="3-5">3–5 years</SelectItem>
                  <SelectItem value="6-10">6–10 years</SelectItem>
                  <SelectItem value="10+">10+ years</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Work Authorization" required>
              <Select value={form.work_authorization} onValueChange={v => set('work_authorization', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="us_citizen">US Citizen</SelectItem>
                  <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                  <SelectItem value="visa_holder">Visa Holder</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Section 3 */}
          <SectionLabel num="3" title="Career Preferences" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Types of Roles Interested In"><Input value={form.role_interests} onChange={e => set('role_interests', e.target.value)} placeholder="Senior Accountant, Controller..." /></FormField>
            <FormField label="Preferred Work Setup" required>
              <Select value={form.preferred_work_setup} onValueChange={v => set('preferred_work_setup', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="onsite">Onsite</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Salary Expectations" optional><Input value={form.salary_expectations} onChange={e => set('salary_expectations', e.target.value)} placeholder="$70,000 - $90,000" /></FormField>
            <FormField label="Open to Relocation?">
              <Select value={form.open_to_relocation} onValueChange={v => set('open_to_relocation', v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Section 4 */}
          <SectionLabel num="4" title="Resume & Screening" />
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="Upload Resume">
                <FileInput file={file} setFile={setFile} accept=".pdf,.doc,.docx" />
              </FormField>
              <FormField label="LinkedIn Profile URL" optional><Input value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." /></FormField>
            </div>
            <FormField label="What are you looking for in your next opportunity?">
              <Textarea value={form.next_opportunity} onChange={e => set('next_opportunity', e.target.value)} placeholder="Growth, culture, work-life balance..." rows={3} />
            </FormField>
          </div>

          <SubmitButton submitting={submitting} label="Submit Application" />
        </form>
      </CardContent>
    </Card>
  );
};


/* ───────────── SHARED COMPONENTS ───────────── */

const SectionLabel = ({ num, title }) => (
  <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
    <span className="w-8 h-8 rounded-lg bg-[#0A1F44] text-white text-sm font-bold flex items-center justify-center">{num}</span>
    <h3 className="text-lg font-semibold text-[#0A1F44]">{title}</h3>
  </div>
);

const FormField = ({ label, required, optional, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
      {optional && <span className="text-gray-400 ml-1 text-xs">(optional)</span>}
    </label>
    {children}
  </div>
);

const FileInput = ({ file, setFile, accept }) => (
  <div className="relative">
    <label className="flex items-center gap-3 px-4 py-2.5 border border-gray-200 rounded-md cursor-pointer hover:border-[#D4AF37] transition-colors bg-white">
      <Upload className="h-4 w-4 text-gray-400" />
      <span className="text-sm text-gray-500 truncate">{file ? file.name : 'Choose file...'}</span>
      <input
        type="file"
        accept={accept}
        className="sr-only"
        onChange={e => setFile(e.target.files[0] || null)}
      />
    </label>
  </div>
);

const SubmitButton = ({ submitting, label }) => (
  <div className="pt-4">
    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg mb-6">
      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center mt-0.5">
        <span className="text-[#0A1F44] text-xs font-bold">i</span>
      </div>
      <p className="text-sm text-gray-600">
        By submitting this form, you agree to be contacted by Omnia Paratus Talent Solutions regarding your inquiry. We respect your privacy and never share your information.
      </p>
    </div>
    <Button
      type="submit"
      size="lg"
      disabled={submitting}
      className="w-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold disabled:opacity-50"
    >
      {submitting ? 'Submitting...' : label}
    </Button>
  </div>
);

const SuccessMessage = ({ type }) => (
  <Card data-testid="submission-success" className="border-2 border-[#D4AF37] shadow-2xl">
    <CardContent className="p-12 text-center">
      <div className="w-20 h-20 mx-auto mb-6 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
        <CheckCircle className="h-10 w-10 text-[#D4AF37]" />
      </div>
      <h2 className="text-3xl font-bold text-[#0A1F44] mb-4">
        {type === 'employer' ? 'Inquiry Received!' : 'Application Received!'}
      </h2>
      <p className="text-lg text-gray-600 max-w-md mx-auto">
        {type === 'employer'
          ? "Thank you for reaching out. We'll review your hiring needs and be in touch within 24 hours."
          : "Thank you for your interest. We'll review your profile and connect with you soon."}
      </p>
    </CardContent>
  </Card>
);

export default Contact;
